import { initialize, change, resetSection, submit } from 'redux-form';
import _ from 'lodash';

import { success, error, loading, handleErrorException } from 'State/actions';
import {
  EDIT_INGREDIENT_FORM_NAME,
  EDIT_RECIPE_FORM_NAME,
  ING_MEASUREMENTS_FORM_NAME,
  ING_PURCHASING_FORM_NAME,
} from 'Common/FormEnum';
import {
  EditIngredientFieldEnum as Fields,
  IngMeasurementsFieldEnum,
  RecipeFieldEnum as RecipeFields,
} from 'Common/FieldEnum';
import * as Selectors from './selectors';
import { convertMNU_IngredientToIngredient } from '../../helpers/ingredient';
import * as types from './types';

export const saveIngredient = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, true));

    try {
      const formValues = { ...data };

      const res = await MPWWebApi.postGenericInsertUpdate(
        'Ingredient/WriteIngredient',
        formValues
      );
      const updatedFields = { ...res.data.Data };

      if (data[Fields.PicturePreview]) {
        const formData = new FormData();
        formData.append('file', data[Fields.PicturePreview]);
        const pictureResult = await MPWWebApi.postGenericFile(
          `ingredient/picture/${res.data.Data.Id}`,
          formData,
          null,
          false
        );
        updatedFields[Fields.Picture] = pictureResult.data.Data;
      }

      dispatch(
        initialize(
          EDIT_INGREDIENT_FORM_NAME,
          convertMNU_IngredientToIngredient(updatedFields)
        )
      );

      const updatedMAPFields = { ...updatedFields[Fields.MeasuresAndCost] };

      dispatch(
        initialize(
          ING_PURCHASING_FORM_NAME,
          _.omit(updatedMAPFields, [
            IngMeasurementsFieldEnum.Measures,
            IngMeasurementsFieldEnum.CostPerGram,
          ])
        )
      );
      dispatch(
        initialize(ING_MEASUREMENTS_FORM_NAME, {
          [IngMeasurementsFieldEnum.Measures]:
            updatedMAPFields[IngMeasurementsFieldEnum.Measures],
          [IngMeasurementsFieldEnum.CostPerGram]:
            updatedMAPFields[IngMeasurementsFieldEnum.CostPerGram],
        })
      );
      dispatch(setSaveSuccessful(res.data.Data.Id));
    } catch (ex) {
      const handledException = handleErrorException(
        ex,
        types.MANAGE_INGREDIENT_FETCH_ERROR,
        {
          Title: 'Error',
          Message: 'Could not save the ingredient.',
        }
      );
      dispatch(handledException);

      /** User tried to delete/update ingredient measure that is used on recipe */
      const errorMessage = _.get(handledException.payload, 'Message', '');
      if (errorMessage.includes('Ingredient Measure')) {
        dispatch(
          resetSection(
            ING_MEASUREMENTS_FORM_NAME,
            IngMeasurementsFieldEnum.Measures
          )
        );
      }
    } finally {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, false));
    }
  };
};

export const getIngredient = (ingredientId, isForDuplicate) => {
  return async (dispatch, getState, MPWWebApi) => {
    const state = getState();
    dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, true));

    try {
      const response = await MPWWebApi.getGeneric(
        `Ingredient/GetIngredientDetail/${ingredientId}`
      );
      const result = response.data.Data;
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, false));

      const ingredient = convertMNU_IngredientToIngredient(result);

      if (isForDuplicate) {
        ingredient.Id = null;
        ingredient.CN_Code = null;
        ingredient.MeasuresAndCost.INV_ItemId = null;

        if (ingredient.MeasuresAndCost.Measures) {
          _.forEach(ingredient.MeasuresAndCost.Measures, (measure) => {
            measure.IngredientId = null;
            measure.CN_Code = null;
          });
        }
      }

      const sourceDesc = ingredient[Fields.SourceDesc];
      const ingredientSourceId = Selectors.getIngredientSourceId(
        state,
        sourceDesc
      );
      const mapFields = {
        ...result[Fields.MeasuresAndCost],
      };

      dispatch(initialize(EDIT_INGREDIENT_FORM_NAME, ingredient));
      dispatch(
        initialize(
          ING_PURCHASING_FORM_NAME,
          _.omit(mapFields, [
            IngMeasurementsFieldEnum.Measures,
            IngMeasurementsFieldEnum.CostPerGram,
          ])
        )
      );
      dispatch(
        initialize(ING_MEASUREMENTS_FORM_NAME, {
          [IngMeasurementsFieldEnum.Measures]:
            mapFields[IngMeasurementsFieldEnum.Measures],
          [IngMeasurementsFieldEnum.CostPerGram]:
            mapFields[IngMeasurementsFieldEnum.CostPerGram],
        })
      );
      dispatch(success(types.MANAGE_INGREDIENT_FETCH_SUCCESS, ingredient));

      if (ingredientSourceId) {
        dispatch(getIngredientGroups(ingredientSourceId));
      }
    } catch (ex) {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, false));
      dispatch(
        handleErrorException(ex, types.MANAGE_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Could not load the ingredient.',
        })
      );
    }
  };
};

export const deleteIngredient = (ingredientId) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, true));
    try {
      await MPWWebApi.postGenericInsertUpdate(
        `Ingredient/Delete/${ingredientId}`
      );
      dispatch(success(types.MANAGE_INGREDIENT_SET_DELETE_SUCCESSFUL, true));
    } catch (ex) {
      const errors = _.get(ex, 'response.data.Errors[0].Data', []);
      if (errors.length) {
        dispatch(
          error(types.MANAGE_INGREDIENT_SET_RECIPES_WITH_INGREDIENT, errors)
        );
      } else {
        dispatch(
          handleErrorException(ex, types.MANAGE_INGREDIENT_FETCH_ERROR, {
            Title: 'Error',
            Message: 'Could not delete the ingredient.',
          })
        );
      }
    } finally {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, false));
    }
  };
};

export const clearErrors = () => {
  return {
    type: types.MANAGE_INGREDIENT_CLEAR_ERRORS,
  };
};

export const setSaveSuccessful = (success) => ({
  type: types.MANAGE_INGREDIENT_SET_SAVE_SUCCESSFUL,
  payload: success,
});

export const setDeleteSuccessful = (success) => ({
  type: types.MANAGE_INGREDIENT_SET_DELETE_SUCCESSFUL,
  payload: success,
});

export const setConvertToRecipeSuccessful = (success) => ({
  type: types.MANAGE_INGREDIENT_SET_CONVERT_TO_RECIPE_SUCCESSFUL,
  payload: success,
});

export const getIngredientSources = () => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING_SOURCES, true));

      const response = await MPWWebApi.getGeneric(
        'Ingredient/GetIngredientSources'
      );
      const result = response.data.Data;

      dispatch(success(types.MANAGE_INGREDIENT_SET_INGREDIENT_SOURCES, result));
    } catch (ex) {
      dispatch(
        handleErrorException(ex, types.MANAGE_INGREDIENT_FETCH_ERROR, {
          Title: 'Error',
          Message: 'Could not load ingredient sources.',
        })
      );
    } finally {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING_SOURCES, false));
    }
  };
};

export const getIngredientGroups = (ingredientSourceId) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING_GROUPS, true));

      const response = await MPWWebApi.getGeneric(
        'Ingredient/GetIngredientGroups'
      );
      const result = response.data.Data;

      dispatch(success(types.MANAGE_INGREDIENT_SET_INGREDIENT_GROUPS, result));
    } catch (ex) {
      dispatch(handleErrorException(ex, types.MANAGE_INGREDIENT_FETCH_ERROR), {
        Title: 'Error',
        Message: 'Could not load ingredient groups.',
      });
    } finally {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING_GROUPS, false));
    }
  };
};

export const clearIngredientGroups = () => ({
  type: types.MANAGE_INGREDIENT_SET_INGREDIENT_GROUPS,
  payload: [],
});

export const convertIngredientToRecipe = (data) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, true));

    try {
      const result = await MPWWebApi.postGenericInsertUpdate(
        'Ingredient/WriteConvertIngredientToRecipe',
        data
      );
      const recipeId = result.data.Data;
      dispatch(change(EDIT_RECIPE_FORM_NAME, RecipeFields.Id, recipeId));
      dispatch(
        change(EDIT_INGREDIENT_FORM_NAME, Fields.ConvertedToRecipeId, recipeId)
      );
      dispatch(
        success(types.MANAGE_INGREDIENT_SET_CONVERT_TO_RECIPE_SUCCESSFUL, true)
      );
    } catch (ex) {
      dispatch(handleErrorException(ex, types.MANAGE_INGREDIENT_FETCH_ERROR), {
        Title: 'Error',
        Message: 'Could not convert ingredient to recipe',
      });
    } finally {
      dispatch(loading(types.MANAGE_INGREDIENT_FETCHING, false));
    }
  };
};

export const submitIngredientForm = () => submit(EDIT_INGREDIENT_FORM_NAME);



// WEBPACK FOOTER //
// ./src/pages/manageingredient/state/editingredient/actions.js